import type { PersonScope } from './crm';
import { MessagingPlatform } from './messagingPlatform.enum';
import type {
  ActionType,
  TriggerKey,
  TriggerType,
} from './userCommunicationAgent';

// Non-platform origins for People that aren't backed by a synced channel.
export enum PersonOrigin {
  MANUAL = 'manual',
  DETACHED = 'detached',
  // A visitor-submitted contact form on a published artifact landing page.
  LANDING_PAGE = 'landing_page',
  TEAM = 'team',
}

export enum PersonActivityType {
  EMAIL = 'EMAIL',
  MESSAGE = 'MESSAGE',
  EVENT = 'EVENT',
}

export interface PersonItem {
  scope: PersonScope; // Logical field — physical key is scopeKey (USER#<userId> or TEAM#<teamId>); PeopleStore maps at the edge.
  // Also written as an attribute — key of the PeopleByPersonId GSI. In clear
  // because it's a GSI key; value is always stripPersonSortKey(sortKey).
  personId: string;
  origin: MessagingPlatform | PersonOrigin;
  syncRef?: string;
  // Per-contact identity (UserContactItem.sourceId) of whichever contact last
  // won the identity write — see buildUpsertFromChannelUpdate.ts isPrimaryContact.
  syncSourceId?: string;
  syncedAt?: number;
  displayName: string;
  normalizedName: string;
  // Team scope only — sort key of the PeopleByScopeReversedName GSI.
  reversedNormalizedName?: string;
  avatarUrl?: string;
  handle?: string;
  // Stored as a DynamoDB String Set; PeopleStore.toPerson() normalizes the SDK Set back to string[]. A direct table reader gets a Set.
  emails: string[];
  // Modeled like emails: manual create/edit (E.164, origin===manual only) or platform sync (best-effort E.164, SET wholesale).
  phones?: string[];
  notes?: string;
  // Manual create/edit when origin===manual; sticky from platform sync otherwise (no GraphQL edit for platform/detached).
  jobTitle?: string;
  // Stored as a DynamoDB String Set (denormalized for owner display + auth); PeopleStore.toPerson() normalizes Set->array, stays undefined when absent.
  sharedWithTeamIds?: string[];
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  // Optimistic-locking counter, incremented on every write — team contacts
  // only; undefined on a personal contact, which has no concurrent writer
  // to guard against.
  version?: number;
  // List (not a Set — avoids Set<->array marshalling) — origins with an active channel.
  origins?: MessagingPlatform[];
  // One avatar per platform; Partial because not every platform has an avatar.
  avatarUrlBySource?: Partial<Record<MessagingPlatform, string>>;
  // Present only on tombstones — personId of the winning person.
  mergedInto?: string;
  mergedAt?: number;
  // Soft-delete tombstone (epoch millis). Set only on synced/detached contacts;
  // manual contacts hard-delete. Queries filter it out; the resync skips it.
  deletedAt?: number;
  // Fields the user has locally edited — the resync must not overwrite them.
  // Values are the LOCKABLE_FIELD constants ("phones" | "jobTitle" | "displayName").
  lockedFields?: string[];
  // Emails the user removed — the resync must not re-ADD them.
  suppressedEmails?: string[];
  // Deterministic hmacEmail() digests of `emails`/`suppressedEmails`/`syncRef`
  // — persisted so equality matches (primary-contact election, reclaim
  // checks, the optimistic-concurrency guard on an email-set write) don't
  // require decrypting `emailsJson` just to compare. A row written before
  // this field existed has none of these yet; matchers fall back to the
  // legacy plaintext columns until the backfill stamps them. See
  // .claude/rules/backend/encryption.md's "PII in a key position" table.
  emailHmacs?: string[];
  suppressedEmailHmacs?: string[];
  syncRefHmac?: string;
  // Deterministic hmacEmail() digest of syncSourceId — see the comment on
  // that field. Same rationale as syncRefHmac: syncSourceId is an email
  // address for Gmail/Outlook contacts, so the equality compare in
  // isPrimaryContact runs on this digest instead of the plaintext value.
  syncSourceIdHmac?: string;
}

// Identity map row — EMAIL# rows in the same `People` table, resolving a
// normalized email to a personId. USER#<userId> scope is written by contact
// ingestion (personKeys.ts) and carries the reclaim/stale-owner machinery in
// emailBinding.ts; TEAM#<teamId> scope is a plain uniqueness index with no
// sync ever contending for it, so it only ever uses the
// attribute_not_exists(sortKey) first-writer-wins put.
export interface PersonEmailBindingItem {
  scopeKey: string; // PK — USER#<userId> or TEAM#<teamId> (same partition as the scope's persons)
  sortKey: string; // SK — EMAIL#<normalizedEmail>
  personId: string;
  createdAt: number;
  updatedAt: number;
}

interface PersonActivityBase {
  personRef: string; // PK — <userId>#<personId>
  // SK — communication: <createdAt(pad)>#<messageId>; event: EVENT#<createdAt(pad)>#<eventId>
  // (disjoint ranges by design — see features/crm/utils/personKeys.ts).
  activityKey: string;
  type: PersonActivityType;
  createdAt: number;
  // createdAt is the message's own timestamp, not when this row was written — projectedAt marks the write.
  projectedAt?: number;
}

export interface CommunicationPersonActivityItem extends PersonActivityBase {
  type: PersonActivityType.EMAIL | PersonActivityType.MESSAGE;
  subject?: string;
  snippet?: string;
  platform: MessagingPlatform;
  direction: 'in' | 'out';
  messageId: string;
  threadMessageId?: string;
  counterpart?: string;
  // The counterpart's address, when it has one. `counterpart` is the label the
  // timeline renders (a display name on Slack rows), so per-address cleanup has
  // to match on this instead. Plaintext on purpose — outside
  // PERSON_ACTIVITY_TARGET_FIELDS so the matcher never needs a KMS decrypt.
  counterpartEmail?: string;
  // Deterministic HMAC-SHA256 of the normalized counterpartEmail (piiHmac.ts,
  // hmacEmail). filterByCounterpart (peopleActivityStore.ts) matches on this
  // when present, falling back to counterpartEmail/counterpart for a row
  // written before this field existed — plaintext counterpartEmail is being
  // phased out the same way UserThreadView.emailAddress is (see FEATURES.md).
  counterpartEmailHmac?: string;
  channelName?: string;
  // TTL applies only to communication rows (180d expiry); events never expire.
  ttl?: number;
}

// Discriminates who wrote a PersonEvent — a flow/agent step or a human. Humans
// are about to write events too (manual timeline entries), so a bare agentId
// string can no longer say which. `agentId`/`userId` deliberately don't unify
// into a single `id` field: keeping the discriminant's payload named after
// what it actually is reads better at every call site than a generic `id`
// the reader has to cross-reference against `kind` to interpret.
export type PersonEventActor =
  | { kind: 'agent'; agentId: string }
  | { kind: 'user'; userId: string };

// apps/api's tsconfig is non-strict, which breaks narrowing on `actor.kind` alone —
// consumers there must use these guards instead of an inline check, the same reason
// isPersonEventActivity/isUserScope exist.
export const isAgentEventActor = (
  actor: PersonEventActor,
): actor is Extract<PersonEventActor, { kind: 'agent' }> =>
  actor.kind === 'agent';

export const isUserEventActor = (
  actor: PersonEventActor,
): actor is Extract<PersonEventActor, { kind: 'user' }> =>
  actor.kind === 'user';

export interface PersonEventFlowStep {
  type: ActionType;
  name?: string;
}

// Snapshot of the flow as it was when the event was recorded. An event is a
// historical record: renaming the flow later must not rewrite the past, and the
// AgentExecutionHistoryRecord — the only other source — expires by TTL.
// `agentUserId` is here because `actor` only keeps the agentId, so an event
// written by a teammate's flow is otherwise unresolvable.
export interface PersonEventFlowProvenance {
  agentId: string;
  agentUserId: string;
  flowName: string;
  triggerKey?: TriggerKey;
  triggerType?: TriggerType;
  steps: PersonEventFlowStep[];
  recordedByStepIndex: number;
}

export interface PersonEventActivityItem extends PersonActivityBase {
  type: PersonActivityType.EVENT;
  eventId: string; // own identifier — does not reuse messageId
  actor: PersonEventActor; // which flow/agent or human wrote the event
  title: string;
  description?: string;
  // Written only by the automation path; absent on a human-written event and on any row predating this field.
  flowProvenance?: PersonEventFlowProvenance;
}

export type PersonActivityItem =
  | CommunicationPersonActivityItem
  | PersonEventActivityItem;

// apps/api's tsconfig is non-strict, which breaks narrowing on `item.type` alone —
// consumers there must use these guards instead of an inline check.
export const isPersonEventActivity = (
  item: PersonActivityItem,
): item is PersonEventActivityItem => item.type === PersonActivityType.EVENT;

export const isCommunicationPersonActivity = (
  item: PersonActivityItem,
): item is CommunicationPersonActivityItem =>
  item.type !== PersonActivityType.EVENT;
